/**
 * PrototypeShell — minimal chrome for isolated design prototypes.
 *
 * Everything below the header is the prototype canvas. The Review Note
 * button in the top-right opens a small panel where each prototype
 * declares:
 *   - What this screen is intended to communicate
 *   - What remains placeholder
 *   - Design decisions still needing approval
 *   - Assumptions made
 *
 * The shell is deliberately un-branded — no sidebar, no Topbar, no login
 * gate. Every prototype is safe to open standalone and safe to revise.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, StickyNote, X } from "lucide-react";

export function PrototypeShell({ title, notes, children, tone = "light" }) {
  const [notesOpen, setNotesOpen] = useState(false);
  const bg = tone === "dark" ? "bg-stone-950 text-stone-100" : "bg-stone-50 text-stone-900";
  return (
    <div className={`min-h-screen ${bg}`} data-testid="prototype-shell">
      <header className="fixed top-0 inset-x-0 z-40 flex items-center justify-between px-6 py-3 text-xs">
        <Link to="/prototypes" className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-900" data-testid="proto-back">
          <ArrowLeft size={13} /> Prototypes
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">{title}</div>
        <button
          onClick={() => setNotesOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-900"
          data-testid="proto-notes-toggle"
        >
          {notesOpen ? <X size={13} /> : <StickyNote size={13} />}
          {notesOpen ? "close" : "review notes"}
        </button>
      </header>
      <main className="pt-12 pb-12" data-testid="prototype-canvas">{children}</main>
      {notesOpen && <NotesPanel notes={notes} onClose={() => setNotesOpen(false)} />}
    </div>
  );
}

function NotesPanel({ notes, onClose }) {
  return (
    <aside
      className="fixed top-12 right-4 bottom-4 w-[380px] max-w-[90vw] z-50 rounded-2xl bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl p-5 overflow-y-auto text-stone-900"
      data-testid="proto-notes-panel"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Review notes</div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={14} /></button>
      </div>
      <Section label="What this screen communicates" body={notes.communicates} />
      <Section label="Placeholder" body={notes.placeholder} />
      <Section label="Decisions pending" body={notes.decisions} />
      <Section label="Assumptions" body={notes.assumptions} />
    </aside>
  );
}

function Section({ label, body }) {
  if (!body) return null;
  const items = Array.isArray(body) ? body : [body];
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">{label}</div>
      <ul className="space-y-1">
        {items.map((line, i) => (
          <li key={i} className="text-[13px] text-stone-700 leading-relaxed">— {line}</li>
        ))}
      </ul>
    </div>
  );
}

export default PrototypeShell;
